/**
 * RESET / PRINT — методы сброса сметы и печати КП.
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: RESET / PRINT).
 *
 *   - reset()       — создать новую смету (с подтверждением)
 *   - sendEmail()   — открыть mailto: с КП
 *   - print()       — построить большую HTML-разметку и вызвать window.print().
 *                     Самый крупный метод (~785 строк HTML-template literals).
 *                     Использует this.state, this.calculate*(), this.formatDate(),
 *                     this.escapeHtml(), this.config.currency.
 *
 * Sprint 19e (2026-05-09): TODO browser-side унификация с
 * services/EstimateTemplate.js (canonical для PDF). Требует:
 *   - ESM-wrapper для shared module (CommonJS → ESM).
 *   - Прокидывание currency-shim через options.fmtCurrency
 *     (browser держит prices в исходных валютах + конвертит в client currency
 *     при выводе; server-side всё уже в USD).
 *   - Регрессионные snapshot-тесты на типовые data scenarios.
 * Сейчас: server (PdfService) использует EstimateTemplate; browser остаётся
 * как был. После регрессионных тестов — перевести browser тоже.
 *
 * @module app/reset-print
 */

/**
 * i18n helper — returns translation if loaded, else fallback (RU text).
 * Safe to call before i18n module has loaded.
 * Supports {param} interpolation via second-arg map.
 *
 * NOTE: print() generates a client-facing PDF (КП / Quote / Cotización). The
 * locale used here is the current UI locale — no per-client preferred_language
 * mechanism exists in the frontend state yet. Backend (services/EstimateTemplate.js
 * + PdfService) uses estimate.preferred_language separately for server-side PDF.
 */
const _t = (k, fb, params) => {
    let v = null;
    try {
        if (window.i18n && typeof window.i18n.t === 'function') {
            v = window.i18n.t(k, params || {});
        }
    } catch (_) { /* ignore */ }
    let out = (v && v !== k) ? v : (fb || k);
    // If i18n didn't run interpolation (fallback path) — do it ourselves.
    if (params && typeof out === 'string') {
        out = out.replace(/\{(\w+)\}/g, (m, name) =>
            Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : m
        );
    }
    return out;
};

/**
 * Прикрепляет методы reset/print к prototype класса.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachResetPrint(ClassRef) {
    Object.assign(ClassRef.prototype, {
        reset() {
            // Sprint 21 W1: confirm только если есть что терять — services/hotels/flights/etc.
            // На страницах Клиенты/Сметы кнопка «+ Новая смета» вызывает reset(), но там
            // нет несохранённых данных → confirm избыточен.
            const hasData = (this.state.services && this.state.services.length > 0)
                || (this.state.hotels && this.state.hotels.length > 0)
                || (this.state.flights && this.state.flights.length > 0)
                || (this.state.otherServices && this.state.otherServices.length > 0)
                || !!this.state.clientName
                || !!this.state.programDescription
                || !!this.state.currentQuoteFile;
            if (hasData && !confirm(_t('print.reset.confirm', 'Создать новую смету? Все несохраненные данные будут потеряны.'))) {
                return;
            }
            {
                this.state.services = [];
                this.state.optionalServices = []; // Сбрасываем доп услуги
                this.state.hotels = []; // Сбрасываем отели
                this.state.flights = []; // Сбрасываем перелёты
                this.state.otherServices = []; // Сбрасываем прочие услуги
                this.state.selectedServices = new Set();
                this.state.hiddenMarkup = 0;
                this.state.taxRate = 0;
                this.state.partnerCommission = 0;
                this.state.tourStart = '';
                this.state.tourEnd = '';
                this.state.clientName = '';
                this.state.clientPhone = '';
                this.state.clientEmail = '';
                this.state.quoteComments = '';
                this.state.programDescription = '';

                // Сбрасываем статус файла - теперь это новая смета
                this.state.currentQuoteFile = '';
                this.state.isQuoteSaved = true; // Новая смета "сохранена" (нечего сохранять)
                this.currentFileHandle = null; // Очищаем ссылку на файл

                // Очищаем автосохранение
                localStorage.removeItem('quoteCalc_autosave');
                localStorage.removeItem('quoteCalc_autosave_timestamp');

                this.loadStateToUI();
                this.renderServices();
                this.renderOptionalServices(); // Обновляем отображение доп услуг
                this.renderHotels(); // Обновляем отели
                this.renderFlights(); // Обновляем перелёты
                this.renderOtherServices(); // Обновляем прочие услуги
                this.updateCalculations();
                this.updateQuoteStatusBar(); // Обновляем статус-бар (скроется)
                this.showNotification(_t('print.reset.created', 'Создана новая смета'));
            }
        },

        sendEmail() {
            // Проверяем наличие email клиента
            if (!this.state.clientEmail) {
                alert(_t('print.email.no_address', 'Пожалуйста, укажите email клиента в блоке данных о клиенте'));
                return;
            }

            // Формируем mailto ссылку с темой и телом письма
            const suffix = this.state.clientName ? ` - ${this.state.clientName}` : '';
            const start = this.state.tourStart ? this.formatDate(this.state.tourStart) : '';
            const end = this.state.tourEnd ? this.formatDate(this.state.tourEnd) : '';
            const subject = encodeURIComponent(
                _t('print.email.subject', 'Коммерческое предложение{suffix}', { suffix })
            );
            const body = encodeURIComponent(
                _t('print.email.body',
                    'Добрый день!\n\nВо вложении коммерческое предложение на тур.\n\nКоличество пассажиров: {pax} чел.\nДаты: {start} - {end}\n\nС уважением,\nMagellania Travel Company',
                    { pax: this.state.paxCount, start, end }
                )
            );

            window.location.href = `mailto:${this.state.clientEmail}?subject=${subject}&body=${body}`;
            this.showNotification(_t('print.email.opened_notification', 'Открыто окно отправки email'));
        },

        // Sprint 19i: V2 печати через shared EstimateTemplate (canonical from PDF service).
        // Цель: A/B сравнить вывод и в будущем заменить старую print() полностью.
        printV2() {
            try {
                const template = window.EstimateTemplate;
                if (!template || typeof template.buildHtml !== 'function') {
                    this.showNotification('EstimateTemplate не загружен — используйте обычную "Печать КП"', true);
                    return;
                }

                let pdfFilename = 'Смета';
                if (this.state.clientName) {
                    pdfFilename = this.state.clientName.replace(/[^a-zA-Z0-9а-яА-Я\s]/g, '').replace(/\s+/g, '_');
                }
                if (this.state.tourStart) pdfFilename += `_${this.state.tourStart}`;
                if (this.state.paxCount > 0) pdfFilename += `_${this.state.paxCount}pax`;

                const originalTitle = document.title;
                document.title = pdfFilename;

                // Multi-currency: нормализуем все цены в USD перед template (template работает в USD)
                const toUsd = (svc) => {
                    if (typeof this.getServicePriceUSD === 'function') {
                        return { ...svc, price: this.getServicePriceUSD(svc), priceCurrency: 'USD' };
                    }
                    return svc;
                };
                const normalize = (arr) => (Array.isArray(arr) ? arr : []).map(toUsd);

                const estimateForTemplate = {
                    ...this.state,
                    services: normalize(this.state.services),
                    hotels: normalize(this.state.hotels),
                    flights: normalize(this.state.flights),
                    otherServices: normalize(this.state.otherServices),
                    optionalServices: normalize(this.state.optionalServices)
                };

                // formatPrintAmount конвертирует USD → client currency
                const fmtClient = (n) => {
                    if (typeof this.formatPrintAmount === 'function') {
                        return this.formatPrintAmount(n);
                    }
                    return '$' + Math.round(n).toLocaleString('en-US');
                };

                const html = template.buildHtml(estimateForTemplate, {
                    fmtCurrency: fmtClient,
                    fmtDate: typeof this.formatDate === 'function' ? this.formatDate.bind(this) : null,
                    esc: typeof this.escapeHtml === 'function' ? this.escapeHtml.bind(this) : null
                });

                // Открываем print preview в новом окне (изолировано — не ломает текущий DOM)
                const printWindow = window.open('', '_blank', 'width=900,height=1200');
                if (!printWindow) {
                    this.showNotification('Разрешите всплывающие окна для печати', true);
                    return;
                }
                printWindow.document.open();
                printWindow.document.write(html);
                printWindow.document.close();
                requestAnimationFrame(() => {
                    printWindow.focus();
                    printWindow.print();
                });
                document.title = originalTitle;
                this.showNotification('Печать V2 (shared template)');
            } catch (error) {
                console.error('printV2 error:', error);
                this.showNotification('Ошибка печати V2: ' + error.message, true);
            }
        },

        print() {
            try {
                // Создаем имя файла для PDF: ФИО заказчика + дата + количество человек
                let pdfFilename = '';

                if (this.state.clientName) {
                    pdfFilename += this.state.clientName.replace(/[^a-zA-Z0-9а-яА-Я\s]/g, '').replace(/\s+/g, '_');
                } else {
                    pdfFilename += _t('pdf.fallback_filename', 'Смета');
                }

                if (this.state.tourStart) {
                    pdfFilename += `_${this.state.tourStart}`;
                } else {
                    const today = new Date().toISOString().split('T')[0];
                    pdfFilename += `_${today}`;
                }

                if (this.state.paxCount && this.state.paxCount > 0) {
                    pdfFilename += `_${this.state.paxCount}pax`;
                }

                // Сохраняем оригинальный заголовок
                const originalTitle = document.title;
                // Устанавливаем новый заголовок для PDF
                document.title = pdfFilename;

                // ─── Phase 8e: Multi-currency normalization для печати ───
                // Все цены приводим к USD ОДИН РАЗ, чтобы все промежуточные
                // расчёты (baseCost, hidden markup proportion, partner commission)
                // велись в одной валюте. Печать выводит финальные суммы в
                // state.clientCurrency через formatPrintAmount().
                //
                // Если currency-shim не загружен (старая среда) — getServicePriceUSD
                // отсутствует, fallback к raw price (полная backward-compat).
                const toUsdPrice = (svc) => {
                    if (typeof this.getServicePriceUSD === 'function') {
                        return this.getServicePriceUSD(svc);
                    }
                    return Number(svc.price) || 0;
                };
                const normalizeArr = (arr) => (Array.isArray(arr) ? arr : []).map(svc => ({
                    ...svc,
                    price: toUsdPrice(svc),
                    _origPriceCurrency: svc.priceCurrency || 'USD'
                }));
                const normalizedServices = normalizeArr(this.state.services);
                const normalizedHotels = normalizeArr(this.state.hotels);
                const normalizedFlights = normalizeArr(this.state.flights);
                const normalizedOtherServices = normalizeArr(this.state.otherServices);

                // Объединяем все услуги из всех секций (в USD)
                const allServices = [
                    ...normalizedServices,
                    ...normalizedHotels,
                    ...normalizedFlights,
                    ...normalizedOtherServices
                ];

                // Sort services by day for printing (используем нормализованную копию)
                const sortedServices = [...normalizedServices].sort((a, b) => a.day - b.day);

                // Calculate base costs from ALL sections (USD)
                const baseCost = allServices.reduce((sum, service) => {
                    return sum + (service.price * service.quantity);
                }, 0);

                // Базовая стоимость только неисключенных услуг для скрытой маржи (USD)
                const baseCostForHiddenMarkup = allServices.reduce((sum, service) => {
                    if (!service.excludeFromMarkup) {
                        return sum + (service.price * service.quantity);
                    }
                    return sum;
                }, 0);

                // Считаем totalWithIndividualMarkup для ВСЕХ секций (USD)
                // calculateServiceTotal обёрнут shim'ом, но т.к. service.price уже в USD
                // (а priceCurrency осталась в _origPriceCurrency), shim не сделает повторной
                // конверсии — он чекает priceCurrency, которая теперь в _origPriceCurrency.
                // Чтобы избежать double-conversion, передаём services с priceCurrency='USD'.
                const allServicesUsd = allServices.map(s => ({ ...s, priceCurrency: 'USD' }));
                const totalWithIndividualMarkup = allServicesUsd.reduce((sum, service) => {
                    return sum + this.calculateServiceTotal(service);
                }, 0);

                // Phase 8e helper: обработать одну (USD-нормализованную) услугу.
                // Все операции в USD, итоговая `displayPrice` хранится тоже в USD —
                // конвертируется в client-currency только при выводе через formatPrintAmount.
                const processService = (service) => {
                    const serviceBaseCost = service.price * service.quantity;
                    // Передаём priceCurrency='USD' — service.price уже в USD после normalizeArr,
                    // shim увидит USD и не будет повторно конвертировать.
                    const usdSvc = { ...service, priceCurrency: 'USD' };
                    const serviceWithMarkup = this.calculateServiceTotal(usdSvc);

                    let hiddenMarkupPortion = 0;
                    if (!service.excludeFromMarkup && baseCostForHiddenMarkup > 0) {
                        const proportion = serviceBaseCost / baseCostForHiddenMarkup;
                        hiddenMarkupPortion = (baseCostForHiddenMarkup * this.state.hiddenMarkup / 100) * proportion;
                    }
                    const totalWithHiddenMarkup = serviceWithMarkup + hiddenMarkupPortion;

                    return {
                        ...service,
                        displayName: service.name,
                        // Храним в USD (НЕ округляем — округлим при выводе)
                        displayPrice: totalWithHiddenMarkup,
                        baseCost: serviceBaseCost
                    };
                };

                // Обрабатываем услуги по дням (основная секция)
                const servicesForPrint = sortedServices.map(processService);

                // Обрабатываем отели / перелёты / прочие услуги
                const hotelsForPrint = normalizedHotels.map(processService);
                const flightsForPrint = normalizedFlights.map(processService);
                const otherServicesForPrint = normalizedOtherServices.map(processService);

                // Объединяем все обработанные услуги
                const allServicesForPrint = [
                    ...servicesForPrint,
                    ...hotelsForPrint,
                    ...flightsForPrint,
                    ...otherServicesForPrint
                ];

                // Рассчитываем комиссию партнера и добавляем к каждой услуге пропорционально
                // Комиссия партнера рассчитывается от суммы ПОСЛЕ добавления скрытой маржи
                if (this.state.partnerCommission > 0) {
                    const totalWithHiddenMarkupForPrint = allServicesForPrint.reduce((sum, service) => sum + service.displayPrice, 0);
                    const totalPartnerCommission = totalWithHiddenMarkupForPrint * (this.state.partnerCommission / 100);

                    // Распределяем комиссию пропорционально базовой стоимости каждой услуги
                    const totalBaseCost = allServicesForPrint.reduce((sum, service) => sum + service.baseCost, 0);

                    allServicesForPrint.forEach(service => {
                        if (totalBaseCost > 0) {
                            const proportion = service.baseCost / totalBaseCost;
                            const partnerCommissionPortion = totalPartnerCommission * proportion;
                            // Phase 8e: храним в USD (без round) — округляем при выводе.
                            service.displayPrice = service.displayPrice + partnerCommissionPortion;
                        }
                    });
                }

                // Итоговая сумма для клиента (без НДС) — ВКЛЮЧАЕТ ВСЕ СЕКЦИИ.
                // В USD; форматирование в client-currency происходит ниже через formatPrintAmount().
                const finalClientTotal = allServicesForPrint.reduce((sum, service) => sum + service.displayPrice, 0);

                // Set print date без проблем с часовыми поясами
                const currentDate = new Date();
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                const currentDateString = `${year}-${month}-${day}`;

                const quoteDateElement = document.getElementById('quote-date');
                if (quoteDateElement) {
                    quoteDateElement.textContent = _t(
                        'pdf.header.date_compiled',
                        'Дата составления: {date}',
                        { date: this.formatDate(currentDateString) }
                    );
                }

                const printContent = document.getElementById('print-content');
                if (!printContent) {
                    this.showNotification(_t('pdf.notify.no_print_target', 'Ошибка: не найден элемент для печати'), true);
                    return;
                }

                // Phase 8e: формат-хелпер. Все суммы в `*ForPrint.displayPrice` уже в USD.
                // Конвертация в client-currency и форматирование (символ + decimals).
                // Если currency-shim не загружен → fallback к старому поведению "$<round>".
                const fmt = (usdAmount) => {
                    if (typeof this.formatPrintAmount === 'function') {
                        return this.formatPrintAmount(usdAmount, 'USD');
                    }
                    const sym = (this.config && this.config.currency) || '$';
                    return `${sym}${Math.round(Number(usdAmount) || 0).toLocaleString('en-US')}`;
                };

                // Информация о валюте для шапки КП
                const currencyInfo = (typeof this.getClientCurrencyInfo === 'function')
                    ? this.getClientCurrencyInfo()
                    : { code: 'USD', symbol: '$', name: _t('pdf.currency.usd_name', 'Доллар США') };

                // Разделяем услуги на две группы: с днями и без дней
                const servicesWithDays = servicesForPrint.filter(s => !s.noDayAssignment);
                const servicesWithoutDays = servicesForPrint.filter(s => s.noDayAssignment);

                // Group services by day for better organization
                const servicesByDay = {};
                servicesWithDays.forEach(service => {
                    if (!servicesByDay[service.day]) {
                        servicesByDay[service.day] = [];
                    }
                    servicesByDay[service.day].push(service);
                });

                // Generate table with day separation
                let tableRows = '';
                const days = Object.keys(servicesByDay).sort((a, b) => parseInt(a) - parseInt(b));

                days.forEach((day, dayIndex) => {
                    const dayServices = servicesByDay[day];

                    // Calculate calendar date for this day
                    const calendarDate = this.calculateActivityDate(parseInt(day));
                    const dateDisplay = calendarDate ? ` (${calendarDate})` : '';

                    // Day header
                    if (days.length > 1) {
                        const dayLabel = _t('pdf.day_label', 'День {n}', { n: day });
                        tableRows += `
                            <tr style="background: #e5e7eb;">
                                <td colspan="3" style="padding: 8pt; border: 2px solid #333; font-weight: 700; text-align: center; font-size: 10pt;">
                                    ${dayLabel}${dateDisplay}
                                </td>
                            </tr>
                        `;
                    }

                    // Services for this day
                    dayServices.forEach(service => {
                        // Формируем строку с временем если оно указано
                        let timeInfo = '';
                        if (service.startTime || service.endTime) {
                            timeInfo = '<br><small style="color: #000; font-size: 8pt;">';
                            if (service.startTime && service.endTime) {
                                const duration = this.calculateDuration(service.startTime, service.endTime);
                                timeInfo += `${service.startTime} - ${service.endTime}`;
                                if (duration) {
                                    timeInfo += ` (${duration})`;
                                }
                            } else if (service.startTime) {
                                timeInfo += _t('pdf.time.from', 'с {time}', { time: service.startTime });
                            } else if (service.endTime) {
                                timeInfo += _t('pdf.time.until', 'до {time}', { time: service.endTime });
                            }
                            timeInfo += '</small>';
                        }

                        const unitPriceUsd = service.quantity > 0
                            ? service.displayPrice / service.quantity
                            : service.displayPrice;
                        tableRows += `
                            <tr>
                                <td style="padding: 5pt; border: 1px solid #ddd; font-size: 9.5pt; line-height: 1.2;">
                                    <strong>${service.displayName}</strong>${timeInfo}<br>
                                    <small style="color: #000; font-size: 8.5pt; line-height: 1.1;">${service.description}</small><br>
                                    <small style="color: #000; font-size: 7.5pt;">${_t('pdf.unit_price', 'Цена за единицу: {price}', { price: fmt(unitPriceUsd) })}</small>
                                </td>
                                <td style="padding: 5pt; border: 1px solid #ddd; text-align: center; font-size: 9pt;">${service.quantity}</td>
                                <td style="padding: 5pt; border: 1px solid #ddd; text-align: right; font-weight: 600; font-size: 9.5pt;">${fmt(service.displayPrice)}</td>
                            </tr>
                        `;
                    });

                    // Add spacing between days (except last day)
                    if (dayIndex < days.length - 1) {
                        tableRows += `
                            <tr>
                                <td colspan="3" style="padding: 4pt; border: none;"></td>
                            </tr>
                        `;
                    }
                });

                const L = {
                    paxCount: _t('pdf.header.pax_count', 'Количество пассажиров'),
                    paxUnit: _t('pdf.header.pax_unit', 'чел.'),
                    tourDates: _t('pdf.header.tour_dates', 'Даты тура'),
                    client: _t('pdf.header.client', 'Заказчик'),
                    contacts: _t('pdf.header.contacts', 'Контакты'),
                    currencyNote: _t('pdf.currency_note', 'Все цены указаны в <strong>{name}</strong> ({symbol})', {
                        name: currencyInfo.name,
                        symbol: currencyInfo.symbol
                    }),
                    programDescription: _t('pdf.program_description', 'Описание программы:'),
                    colService: _t('pdf.column.service', 'Услуга'),
                    colQty: _t('pdf.column.quantity', 'Кол-во'),
                    colQtyShort: _t('pdf.column.quantity_short', 'Кол.'),
                    colCost: _t('pdf.column.cost', 'Стоимость'),
                    colPrice: _t('pdf.column.price', 'Цена'),
                    colDay: _t('pdf.column.day', 'День'),
                    colNights: _t('pdf.column.nights', 'Ночей'),
                    colPassengers: _t('pdf.column.passengers', 'Пассажиров'),
                    colHotel: _t('pdf.column.hotel', 'Отель'),
                    colFlight: _t('pdf.column.flight', 'Рейс'),
                    colDeadline: _t('pdf.column.deadline', 'Срок оплаты'),
                    colAmount: _t('pdf.column.amount', 'Сумма'),
                    sectionHotels: _t('pdf.section.hotels', 'Отели и размещение'),
                    sectionFlights: _t('pdf.section.flights', 'Перелёты'),
                    sectionOther: _t('pdf.section.other', 'Прочие услуги'),
                    sectionNoDay: _t('pdf.section.no_day', 'Дополнительные услуги (не привязаны к конкретным дням):'),
                    sectionNoDayNote: _t('pdf.section.no_day_note', '* Данные услуги включены в основную стоимость тура и не привязаны к конкретным дням программы (перелёты, визы, страховки, и т.д.)'),
                    sectionOptional: _t('pdf.section.optional', 'Опциональные услуги (на выбор):'),
                    sectionOptionalNote: _t('pdf.section.optional_note', '* Опциональные услуги оплачиваются дополнительно и не входят в основную стоимость тура'),
                    total: _t('pdf.total', 'ИТОГО'),
                    bookingTerms: _t('pdf.booking_terms', 'Условия бронирования:'),
                    prepayTitle: _t('pdf.prepayment.title', 'Предоплата'),
                    prepayIntro: _t('pdf.prepayment.intro', 'Для бронирования следующих услуг требуется внести предоплату:'),
                    prepayTotal: _t('pdf.prepayment.total', 'ИТОГО ПРЕДОПЛАТА:'),
                    prepayNoDate: _t('pdf.prepayment.no_date', '—')
                };

                printContent.innerHTML = `
                    <p><strong>${L.paxCount}:</strong> ${this.state.paxCount} ${L.paxUnit}</p>
                    ${this.state.tourStart ? `<p><strong>${L.tourDates}:</strong> ${this.formatDate(this.state.tourStart)} ${this.state.tourEnd ? '- ' + this.formatDate(this.state.tourEnd) : ''}</p>` : ''}
                    ${this.state.clientName ? `<p><strong>${L.client}:</strong> ${this.state.clientName}</p>` : ''}
                    ${this.state.clientPhone ? `<p><strong>${L.contacts}:</strong> ${this.state.clientPhone} ${this.state.clientEmail ? ', ' + this.state.clientEmail : ''}</p>` : ''}
                    <div style="font-size: 10pt; color: #555; margin: 8pt 0; padding: 6pt 10pt; background: #f9fafb; border-left: 3px solid #2563eb;">
                        ${L.currencyNote}
                    </div>
                    ${this.state.programDescription && this.state.programDescription.replace(/<[^>]*>/g, '').trim() ? `
                        <div style="margin: 0.5cm 0; padding: 12pt; background: #f9fafb;">
                            <p style="margin: 0 0 8pt 0; font-weight: 700; font-size: 10pt;">${L.programDescription}</p>
                            <div style="line-height: 1.4; font-size: 9.5pt;">${this.state.programDescription}</div>
                        </div>
                    ` : ''}
                    <br>
                    <table style="width: 100%; border-collapse: collapse; margin: 0.5cm 0;">
                        <thead>
                            <tr style="background: #f5f5f5;">
                                <th style="padding: 6pt; border: 1px solid #ddd; font-size: 9.5pt; font-weight: 600;">${L.colService}</th>
                                <th style="padding: 6pt; border: 1px solid #ddd; font-size: 9.5pt; font-weight: 600;">${L.colQty}</th>
                                <th style="padding: 6pt; border: 1px solid #ddd; font-size: 9.5pt; font-weight: 600;">${L.colCost}</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${tableRows}
                        </tbody>
                    </table>

                    ${hotelsForPrint.length > 0 ? `
                        <div style="margin-top: 0.6cm; padding-top: 0.4cm; border-top: 2px dashed #aaa;">
                            <h3 style="margin: 0 0 0.3cm 0; font-size: 11pt; font-weight: 700;">${L.sectionHotels}</h3>
                            <table style="width: 100%; border-collapse: collapse; font-size: 9pt;">
                                <thead>
                                    <tr style="background: #f5f5f5;">
                                        <th style="padding: 5pt; border: 1px solid #ddd; text-align: left; width: 65%; font-weight: 600;">${L.colHotel}</th>
                                        <th style="padding: 5pt; border: 1px solid #ddd; text-align: center; width: 12%; font-weight: 600;">${L.colNights}</th>
                                        <th style="padding: 5pt; border: 1px solid #ddd; text-align: right; width: 23%; font-weight: 600;">${L.colCost}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${hotelsForPrint.map(service => `
                                        <tr>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; line-height: 1.2;">
                                                <strong>${service.displayName}</strong>${service.region ? '<br><small style="color: #000; font-size: 8pt;">' + service.region + '</small>' : ''}${service.description && service.description !== service.displayName ? '<br><small style="color: #000; font-size: 8pt;">' + service.description + '</small>' : ''}
                                            </td>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: center;">${service.quantity}</td>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: right; font-weight: 600;">${fmt(service.displayPrice)}</td>
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>
                        </div>
                    ` : ''}

                    ${flightsForPrint.length > 0 ? `
                        <div style="margin-top: 0.6cm; padding-top: 0.4cm; border-top: 2px dashed #aaa;">
                            <h3 style="margin: 0 0 0.3cm 0; font-size: 11pt; font-weight: 700;">${L.sectionFlights}</h3>
                            <table style="width: 100%; border-collapse: collapse; font-size: 9pt;">
                                <thead>
                                    <tr style="background: #f5f5f5;">
                                        <th style="padding: 5pt; border: 1px solid #ddd; text-align: left; width: 65%; font-weight: 600;">${L.colFlight}</th>
                                        <th style="padding: 5pt; border: 1px solid #ddd; text-align: center; width: 12%; font-weight: 600;">${L.colPassengers}</th>
                                        <th style="padding: 5pt; border: 1px solid #ddd; text-align: right; width: 23%; font-weight: 600;">${L.colCost}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${flightsForPrint.map(service => `
                                        <tr>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; line-height: 1.2;">
                                                <strong>${service.displayName}</strong>${service.description && service.description !== service.displayName ? '<br><small style="color: #000; font-size: 8pt;">' + service.description + '</small>' : ''}
                                            </td>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: center;">${service.quantity}</td>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: right; font-weight: 600;">${fmt(service.displayPrice)}</td>
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>
                        </div>
                    ` : ''}

                    ${otherServicesForPrint.length > 0 ? `
                        <div style="margin-top: 0.6cm; padding-top: 0.4cm; border-top: 2px dashed #aaa;">
                            <h3 style="margin: 0 0 0.3cm 0; font-size: 11pt; font-weight: 700;">${L.sectionOther}</h3>
                            <table style="width: 100%; border-collapse: collapse; font-size: 9pt;">
                                <thead>
                                    <tr style="background: #f5f5f5;">
                                        <th style="padding: 5pt; border: 1px solid #ddd; text-align: left; width: 65%; font-weight: 600;">${L.colService}</th>
                                        <th style="padding: 5pt; border: 1px solid #ddd; text-align: center; width: 12%; font-weight: 600;">${L.colQty}</th>
                                        <th style="padding: 5pt; border: 1px solid #ddd; text-align: right; width: 23%; font-weight: 600;">${L.colCost}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${otherServicesForPrint.map(service => `
                                        <tr>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; line-height: 1.2;">
                                                <strong>${service.displayName}</strong>${service.description && service.description !== service.displayName ? '<br><small style="color: #000; font-size: 8pt;">' + service.description + '</small>' : ''}
                                            </td>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: center;">${service.quantity}</td>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: right; font-weight: 600;">${fmt(service.displayPrice)}</td>
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>
                        </div>
                    ` : ''}

                    <div style="text-align: right; font-size: 16pt; font-weight: 700; margin-top: 0.5cm; border-top: 2px solid #000; padding-top: 0.3cm;">
                        ${L.total}: ${fmt(finalClientTotal)}
                    </div>

                    ${this.state.bookingTerms ? `
                        <div style="margin-top: 0.8cm; padding: 12pt; background: #f9fafb; border: 1px solid #ddd;">
                            <p style="margin: 0 0 8pt 0; font-weight: 700; font-size: 10pt;">${L.bookingTerms}</p>
                            <div style="line-height: 1.4; font-size: 9pt;">${this.state.bookingTerms}</div>
                        </div>
                    ` : ''}

                    ${servicesWithoutDays.length > 0 ? `
                        <div style="margin-top: 0.8cm; padding-top: 0.4cm; border-top: 2px dashed #aaa;">
                            <h3 style="margin: 0 0 0.2cm 0; font-size: 11pt; font-weight: 700;">${L.sectionNoDay}</h3>
                            <table style="width: 100%; border-collapse: collapse; font-size: 8.5pt;">
                                <thead>
                                    <tr style="background: #f5f5f5;">
                                        <th style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: left; width: 65%; font-weight: 600;">${L.colService}</th>
                                        <th style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: center; width: 12%; font-weight: 600;">${L.colQty}</th>
                                        <th style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: right; width: 23%; font-weight: 600;">${L.colCost}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${servicesWithoutDays.map(service => `
                                        <tr>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; line-height: 1.2;">
                                                <strong>${service.displayName}</strong>${service.description && service.description !== service.displayName ? '<br><small style="color: #000; font-size: 8pt;">' + service.description + '</small>' : ''}
                                            </td>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: center;">${service.quantity}</td>
                                            <td style="padding: 4pt 5pt; border: 1px solid #ddd; text-align: right; font-weight: 600;">${fmt(service.displayPrice)}</td>
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>
                            <p style="margin-top: 0.2cm; color: #000; font-size: 7.5pt; font-style: italic; line-height: 1.2;">
                                ${L.sectionNoDayNote}
                            </p>
                        </div>
                    ` : ''}

                    ${this.state.optionalServices && this.state.optionalServices.length > 0 ? `
                        <div style="margin-top: 0.8cm; padding-top: 0.4cm; border-top: 2px dashed #aaa;">
                            <h3 style="margin: 0 0 0.2cm 0; color: #000; font-size: 11pt; font-weight: 600;">${L.sectionOptional}</h3>
                            <table style="width: 100%; border-collapse: collapse; font-size: 8pt;">
                                <thead>
                                    <tr style="background: #f5f5f5;">
                                        <th style="padding: 3pt 4pt; border: 1px solid #ddd; text-align: left; width: 60%; font-weight: 600;">${L.colService}</th>
                                        <th style="padding: 3pt 4pt; border: 1px solid #ddd; text-align: center; width: 12%; font-weight: 600;">${L.colDay}</th>
                                        <th style="padding: 3pt 4pt; border: 1px solid #ddd; text-align: center; width: 8%; font-weight: 600;">${L.colQtyShort}</th>
                                        <th style="padding: 3pt 4pt; border: 1px solid #ddd; text-align: right; width: 20%; font-weight: 600;">${L.colPrice}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${this.state.optionalServices.map(service => {
                                        const calendarDate = this.calculateActivityDate(service.day);
                                        // calculateServiceTotal обёрнут shim'ом — он сам конвертит
                                        // priceCurrency → USD. Возврат в USD.
                                        const serviceTotal = this.calculateServiceTotal(service);
                                        return `
                                            <tr>
                                                <td style="padding: 3pt 4pt; border: 1px solid #ddd; line-height: 1.2;">
                                                    ${service.name}${service.description && service.description !== service.name ? ' — ' + service.description : ''}
                                                </td>
                                                <td style="padding: 3pt 4pt; border: 1px solid #ddd; text-align: center;">
                                                    ${service.day}${calendarDate ? ' (' + calendarDate + ')' : ''}
                                                </td>
                                                <td style="padding: 3pt 4pt; border: 1px solid #ddd; text-align: center;">${service.quantity}</td>
                                                <td style="padding: 3pt 4pt; border: 1px solid #ddd; text-align: right; font-weight: 600;">${fmt(serviceTotal)}</td>
                                            </tr>
                                        `;
                                    }).join('')}
                                </tbody>
                            </table>
                            <p style="margin-top: 0.2cm; color: #000; font-size: 7.5pt; font-style: italic; line-height: 1.2;">
                                ${L.sectionOptionalNote}
                            </p>
                        </div>
                    ` : ''}

                    ${(() => {
                        // Phase 8e: prepayment в исходной валюте услуги (_origPriceCurrency).
                        // Конвертируем в USD через convertForPrint, агрегируем total в USD,
                        // выводим в client-currency через fmt().
                        const convertPrepay = (raw, origCur) => {
                            if (typeof this.convertForPrint !== 'function') return Number(raw) || 0;
                            // raw в исходной валюте → конвертируем в USD
                            // Используем low-level convertWithFees через cache, не convertForPrint
                            // (тот идёт в client-currency, нам нужен USD-pivot).
                            const utils = window.CurrencyUtils;
                            const cache = this._currencyCache;
                            const num = Number(raw) || 0;
                            const from = origCur || 'USD';
                            if (from === 'USD' || !utils || !cache || !cache.rates) return num;
                            try {
                                const { converted } = utils.convertWithFees(
                                    num, from, 'USD', cache.rates, cache.fees || {}
                                );
                                return converted;
                            } catch (_) {
                                return num;
                            }
                        };

                        // Собираем все услуги с предоплатой (>0)
                        const servicesWithPrepayment = allServicesForPrint
                            .map(s => ({
                                ...s,
                                _prepayUsd: convertPrepay(s.prepayment, s._origPriceCurrency)
                            }))
                            .filter(s => s._prepayUsd > 0);

                        if (servicesWithPrepayment.length === 0) {
                            return ''; // Не показываем блок если нет предоплат
                        }

                        // Считаем общую сумму предоплаты в USD
                        const totalPrepaymentUsd = servicesWithPrepayment.reduce(
                            (sum, s) => sum + s._prepayUsd, 0
                        );

                        return `
                            <div style="margin-top: 0.8cm; padding-top: 0.4cm; border-top: 2px solid #ddd;">
                                <h3 style="margin: 0 0 0.3cm 0; font-size: 11pt; font-weight: 700;">${L.prepayTitle}</h3>
                                <p style="margin: 0 0 0.2cm 0; font-size: 8.5pt; line-height: 1.3;">
                                    ${L.prepayIntro}
                                </p>
                                <table style="width: 100%; border-collapse: collapse; font-size: 9pt;">
                                    <thead>
                                        <tr style="background: #f5f5f5;">
                                            <th style="padding: 6pt; border: 1px solid #ddd; text-align: left; font-weight: 600;">${L.colService}</th>
                                            <th style="padding: 6pt; border: 1px solid #ddd; text-align: center; font-weight: 600; width: 25%;">${L.colDeadline}</th>
                                            <th style="padding: 6pt; border: 1px solid #ddd; text-align: right; font-weight: 600; width: 20%;">${L.colAmount}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${servicesWithPrepayment.map(service => {
                                            return `
                                                <tr>
                                                    <td style="padding: 5pt; border: 1px solid #ddd; line-height: 1.2;">
                                                        ${service.displayName}
                                                    </td>
                                                    <td style="padding: 5pt; border: 1px solid #ddd; text-align: center; font-size: 8.5pt;">
                                                        ${service.prepaymentDeadline ? this.formatDate(service.prepaymentDeadline) : L.prepayNoDate}
                                                    </td>
                                                    <td style="padding: 5pt; border: 1px solid #ddd; text-align: right; font-weight: 600;">
                                                        ${fmt(service._prepayUsd)}
                                                    </td>
                                                </tr>
                                            `;
                                        }).join('')}
                                        <!-- 🔧 FIX (P0): итоговая строка в tbody, не в tfoot —
                                             иначе браузер повторяет tfoot на каждой странице при печати -->
                                        <tr style="background: #f5f5f5; page-break-inside: avoid;">
                                            <td colspan="2" style="padding: 8pt; border: 2px solid #333; text-align: right; font-weight: 700; font-size: 10pt;">
                                                ${L.prepayTotal}
                                            </td>
                                            <td style="padding: 8pt; border: 2px solid #333; text-align: right; font-weight: 700; font-size: 11pt;">
                                                ${fmt(totalPrepaymentUsd)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        `;
                    })()}
                `;

                // Sprint 19a: requestAnimationFrame вместо 300ms setTimeout — даём
                // браузеру 1 frame чтобы наложить новый innerHTML, потом сразу print().
                // Хардкод 300ms был "на всякий случай" — без обоснования.
                requestAnimationFrame(() => {
                    window.print();
                    document.title = originalTitle;
                    this.showNotification(_t('pdf.notify.sent_to_print', 'Документ отправлен на печать'));
                });
            } catch (error) {
                this.showNotification(
                    _t('pdf.notify.print_error', 'Ошибка печати: {message}', { message: error.message }),
                    true
                );
            }
        },

        // Открытие файла через File System Access API
        async openQuoteFile() {
            try {
                // Проверяем поддержку File System Access API
                if ('showOpenFilePicker' in window) {
                    const [fileHandle] = await window.showOpenFilePicker({
                        types: [{
                            description: 'Quote Files',
                            accept: {
                                'application/json': ['.json'],
                                'text/csv': ['.csv']
                            }
                        }],
                        multiple: false
                    });

                    const file = await fileHandle.getFile();
                    const content = await file.text();

                    // Определяем тип файла
                    const extension = file.name.split('.').pop().toLowerCase();

                    try {
                        if (extension === 'json') {
                            const quoteData = JSON.parse(content);

                            // Version validation
                            if (!quoteData.version) {
                                this.showNotification('Файл не содержит информации о версии', true);
                                return;
                            }

                            if (quoteData.version > this.QUOTE_VERSION) {
                                this.showNotification(`Файл создан в более новой версии (${quoteData.version}). Текущая версия: ${this.QUOTE_VERSION}`, true);
                                return;
                            }

                            // Apply metadata (с миграцией старых полей)
                            if (quoteData.metadata) {
                                // Копируем metadata с переименованием quoteDescription → quoteComments
                                const metadata = { ...quoteData.metadata };
                                // Миграция: если есть старое поле quoteDescription, переименуем в quoteComments
                                if ('quoteDescription' in metadata && !('quoteComments' in metadata)) {
                                    metadata.quoteComments = metadata.quoteDescription;
                                    delete metadata.quoteDescription;
                                }
                                Object.assign(this.state, metadata);
                            }

                            // Apply services
                            if (quoteData.services) {
                                this.state.services = quoteData.services.map(service => ({
                                    ...service,
                                    excludeFromMarkup: service.excludeFromMarkup || false
                                }));
                                this.state.selectedServices = new Set();
                            }

                            // Apply optional services
                            if (quoteData.optionalServices) {
                                this.state.optionalServices = quoteData.optionalServices;
                            } else {
                                this.state.optionalServices = [];
                            }

                            // Apply hotels
                            if (quoteData.hotels) {
                                this.state.hotels = quoteData.hotels;
                            } else {
                                this.state.hotels = [];
                            }

                            // Apply flights
                            if (quoteData.flights) {
                                this.state.flights = quoteData.flights;
                            } else {
                                this.state.flights = [];
                            }

                            // Apply other services
                            if (quoteData.otherServices) {
                                this.state.otherServices = quoteData.otherServices;
                            } else {
                                this.state.otherServices = [];
                            }

                            this.loadStateToUI();
                            this.renderServices();
                            this.renderOptionalServices();
                            this.renderHotels();
                            this.renderFlights();
                            this.renderOtherServices();
                            this.updateCalculations();

                            // Сохраняем fileHandle для последующей перезаписи
                            this.currentFileHandle = fileHandle;
                            this.state.currentQuoteFile = file.name;
                            this.state.isQuoteSaved = true;

                            // Сохраняем последнюю загруженную смету в localStorage для автозагрузки
                            localStorage.setItem('quoteCalc_lastQuote', JSON.stringify(quoteData));
                            localStorage.setItem('quoteCalc_lastQuoteFileName', file.name);

                            // Очищаем автосохранение (загружена сохранённая смета)
                            localStorage.removeItem('quoteCalc_autosave');
                            localStorage.removeItem('quoteCalc_autosave_timestamp');

                            // Получаем папку из fileHandle (если возможно)
                            // Примечание: File System Access API не дает прямой доступ к родительской папке
                            // Но мы можем запомнить directoryHandle при "Сохранить как"

                            this.updateQuoteStatusBar();
                            this.updateFiltersVisibility();

                            this.showNotification(`Смета загружена: ${file.name}`);
                        } else if (extension === 'csv') {
                            // CSV loading через старый метод
                            this.showNotification('CSV формат поддерживается только через обычное открытие файла', true);
                        }
                    } catch (error) {
                        this.showNotification('Ошибка загрузки файла: ' + error.message, true);
                    }
                } else {
                    // Fallback на старый метод
                    document.getElementById('load-quote').click();
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    this.showNotification('Ошибка открытия файла: ' + error.message, true);
                }
            }
        },

        // Сохранить как... (выбрать новую папку/файл)
        async saveQuoteAsJSON() {
            try {
                if ('showSaveFilePicker' in window) {
                    const quoteData = {
                        version: this.QUOTE_VERSION,
                        appVersion: this.APP_VERSION,
                        timestamp: new Date().toISOString(),
                        metadata: {
                            paxCount: this.state.paxCount,
                            tourStart: this.state.tourStart,
                            tourEnd: this.state.tourEnd,
                            clientName: this.state.clientName,
                            clientPhone: this.state.clientPhone,
                            clientEmail: this.state.clientEmail,
                            quoteComments: this.state.quoteComments,
                            programDescription: this.state.programDescription,
                            hiddenMarkup: this.state.hiddenMarkup,
                            partnerCommission: this.state.partnerCommission
                        },
                        services: this.state.services,
                        calculations: {
                            baseCost: this.calculateBaseCost(),
                            totalProfit: this.calculateTotalProfit(),
                            clientTotal: this.calculateClientTotal()
                        }
                    };

                    // Генерируем предложенное имя файла
                    let suggestedName = '';
                    if (this.state.clientName) {
                        suggestedName += this.state.clientName.replace(/[^a-zA-Z0-9а-яА-Я\s]/g, '').replace(/\s+/g, '_');
                    } else {
                        suggestedName += 'Смета';
                    }
                    if (this.state.tourStart) {
                        suggestedName += `_${this.state.tourStart}`;
                    } else {
                        const today = new Date().toISOString().split('T')[0];
                        suggestedName += `_${today}`;
                    }
                    if (this.state.paxCount && this.state.paxCount > 0) {
                        suggestedName += `_${this.state.paxCount}pax`;
                    }
                    suggestedName += '.json';

                    const fileHandle = await window.showSaveFilePicker({
                        suggestedName: suggestedName,
                        types: [{
                            description: 'JSON Quote File',
                            accept: { 'application/json': ['.json'] }
                        }]
                    });

                    const writable = await fileHandle.createWritable();
                    await writable.write(JSON.stringify(quoteData, null, 2));
                    await writable.close();

                    // Обновляем состояние
                    this.currentFileHandle = fileHandle;
                    this.state.currentQuoteFile = fileHandle.name;
                    this.state.isQuoteSaved = true;
                    this.updateQuoteStatusBar();

                    this.showNotification(`Смета сохранена: ${fileHandle.name}`);
                } else {
                    // Fallback на старый метод
                    this.saveQuoteJSON();
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    this.showNotification('Ошибка сохранения: ' + error.message, true);
                }
            }
        },

        // Функция выбора папки для сохранения (если поддерживается браузером)
        async selectSaveFolder() {
            try {
                // Проверяем поддержку File System Access API
                if ('showDirectoryPicker' in window) {
                    const directoryHandle = await window.showDirectoryPicker();
                    this.state.saveFolder = directoryHandle.name;
                    localStorage.setItem('quoteCalc_saveFolder', directoryHandle.name);
                    this.directoryHandle = directoryHandle;

                    // Обновляем кнопку
                    const btn = document.getElementById('select-folder-btn');
                    if (btn) {
                        btn.title = `Папка: ${directoryHandle.name}`;
                        btn.style.background = '#16a34a';
                    }

                    this.showNotification(`Папка выбрана: ${directoryHandle.name}`);
                } else {
                    // Для браузеров без поддержки File System Access API
                    this.showNotification('Ваш браузер не поддерживает выбор папки. Файлы сохраняются в папку "Загрузки"', false);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    this.showNotification('Ошибка выбора папки: ' + error.message, true);
                }
            }
        },

        // Сохранение файла в выбранную папку (если поддерживается)
        async saveToSelectedFolder(content, filename) {
            try {
                if (this.directoryHandle && 'createWritable' in FileSystemFileHandle.prototype) {
                    const fileHandle = await this.directoryHandle.getFileHandle(filename, { create: true });
                    const writable = await fileHandle.createWritable();
                    await writable.write(content);
                    await writable.close();

                    // Обновляем состояние при успешном сохранении
                    this.state.currentQuoteFile = filename;
                    this.state.isQuoteSaved = true;
                    this.state.lastSaveFolder = this.directoryHandle.name;
                    this.updateQuoteStatusBar();

                    return true;
                }
                return false;
            } catch (error) {
                console.warn('Не удалось сохранить в выбранную папку:', error);
                return false;
            }
        },
    });
}
